import PropTypes from 'prop-types';
import any from "ramda/es/any";
import equals from "ramda/es/equals";
import cx from "ramda-extension/es/cx";
import isNotNil from "ramda-extension/es/isNotNil";
import React, { Component, Fragment } from 'react';
import { createPortal, findDOMNode } from 'react-dom';
import { Manager, Popper, Reference } from 'react-popper';
import { withDefaultComponentProps } from '../../../decorators/withDefaultComponentProps';
import FormGroup from '../../../molecules/FormGroup';
import SlideIn from '../../../molecules/SlideIn';
import { validationDisplayTypePropType } from '../../../molecules/Validation/propTypes';
import Calendar from '../../Calendar';
import SvgCalendarIcon from '../../SvgIcon/Icons/Calendar';
import { composedSimpleTextFieldWithoutValidations as SimpleTextField } from '../../TextField/SimpleTextField/SimpleTextField';
import { composedTextFieldWithoutValidations as TextField } from '../../TextField/TextField';
import DatePickerPanelControls from '../DatePickerPanelControls';
const COMPONENT_NAME = 'DatePicker';
const outsideClickIgnoreClass = 'react-datepicker-ignore-onclickoutside';
class DatePicker extends Component {
    constructor(props){
        super(props);
        this.state = {
            open: false
        };
    }
    handleInputBlur = ({ target: { value } })=>{
        if (!this.state.open && isNotNil(value)) {
            this.props.onLeave?.(null, value);
        }
    };
    blur() {
        this.setOpen(false);
        this.props.onBlur?.();
    }
    focus(event) {
        if (!this.props.openCalendarByIcon) {
            this.setOpen(true);
        }
        this.props.onFocus?.(event);
    }
    changeValue = (event, newValue)=>{
        this.props.onChange?.(event, newValue);
    };
    onInputKeyDown = (event)=>{
        const eventKey = event.key;
        const comparedKeys = [
            'Enter',
            'Escape',
            'Tab'
        ];
        if (any(equals(eventKey))(comparedKeys)) {
            this.setOpen(false);
            this.props.onChange?.(event, this.props.value);
            event.target.blur();
        }
    };
    handleInputChange = (e, value)=>{
        this.props.onChange?.(e, value);
    };
    setOpen = (open)=>{
        const thisElement = findDOMNode(this);
        this.setState({
            open,
            target: open ? thisElement : null,
            inputReference: open ? thisElement.querySelector('.Input') : null
        });
    };
    handleRootClick = (event)=>{
        if (!event.isDefaultPrevented()) {
            event.preventDefault();
            if (!this.props.disabled && !this.props.readOnly && !this.state.open) {
                this.focus(event);
            }
        }
    };
    handleCalendarClickOutside = ()=>{
        this.blur();
    };
    handleSelect = (date)=>{
        this.changeValue(null, date);
        this.props.onLeave?.(null, date);
        this.blur();
    };
    handleClickPanelSelect = (event)=>{
        if (this.props.openCalendarByIcon) {
            this.setOpen(true);
        }
        event.preventDefault();
        this.focus(event);
    };
    handleClickPanelClear = (e)=>{
        e.preventDefault();
        this.changeValue(e, '');
        this.setOpen(false);
        this.props.onClickPanelClear?.();
    };
    renderPanelControls() {
        const { controlPanelIconOpen, controlPanelIconClose, disabled, clearable, readOnly, value } = this.props;
        const panelProps = {
            controlPanelIconOpen,
            controlPanelIconClose,
            disabled,
            isOpen: this.state.open,
            value,
            clearable,
            readOnly,
            onClear: this.handleClickPanelClear,
            onSelect: this.handleClickPanelSelect
        };
        return /*#__PURE__*/ React.createElement(DatePickerPanelControls, panelProps);
    }
    createCalendarKeySuffix() {
        const { reactive, value } = this.props;
        return reactive ? `.${value}` : '';
    }
    renderCalendar = ()=>{
        const { hideCalendarControls, locale, disabled, readOnly, value } = this.props;
        const hideCalendar = disabled || readOnly || !this.state.open;
        const Wrapper = this.props.reactive ? Fragment : SlideIn;
        return /*#__PURE__*/ React.createElement(Wrapper, null, hideCalendar ? null : /*#__PURE__*/ React.createElement(Calendar, {
            key: `calendar${this.createCalendarKeySuffix()}`,
            value: value,
            hideCalendarHeader: true,
            onSelect: this.handleSelect,
            onClickOutside: this.handleCalendarClickOutside,
            locale: locale,
            dateFormatCalendar: this.props.dateFormatCalendar,
            dateFormat: this.props.dateFormat,
            minDate: this.props.minDate,
            maxDate: this.props.maxDate,
            startDate: this.props.startDate,
            endDate: this.props.endDate,
            openToDate: this.props.openToDate,
            excludeDates: this.props.excludeDates,
            filterDate: this.props.filterDate,
            includeDates: this.props.includeDates,
            showYearDropdown: this.props.showYearDropdown,
            todayButton: this.props.todayButton,
            outsideClickIgnoreClass: outsideClickIgnoreClass,
            hideCalendarControls: hideCalendarControls,
            sameDayLabel: this.props.sameDayLabel,
            nextDayLabel: this.props.nextDayLabel,
            dayAfterTomorrowLabel: this.props.dayAfterTomorrowLabel
        }));
    };
    render() {
        const calendar = this.renderCalendar();
        const { asyncValidationInProgress, id, labelText, labelNote, labelPosition, labelClassName, className, floatingLabelText, floatingLabelClassName, controlClassName, controlInputClassName, validationText, validationClassName, hideValidationText, disabled, readOnly, tooltip, autoComplete, placeholder, value, maxLength, outline, sizeSmall, calendarTargetContainer, datePickerPlacement, required, validationDisplayType, popperModifiers, keyboardInput } = this.props;
        const textfieldProps = {
            asyncValidationInProgress,
            id,
            labelText,
            tooltip,
            labelNote,
            labelPosition,
            labelClassName,
            floatingLabelText,
            floatingLabelClassName,
            controlClassName,
            controlInputClassName,
            validationText,
            validationClassName,
            validationDisplayType,
            hideValidationText,
            disabled,
            autoComplete,
            placeholder,
            value,
            maxLength,
            panelControls: this.renderPanelControls(),
            ref: (el)=>{
                this.input = el;
            },
            onChange: this.handleInputChange,
            onKeyDown: this.onInputKeyDown,
            outline,
            required,
            sizeSmall
        };
        const TextFieldType = this.props.simple ? SimpleTextField : TextField;
        const textField = /*#__PURE__*/ React.createElement("div", {
            className: "react-datepicker__input-container",
            style: {
                outline: 'none'
            },
            onBlur: this.handleInputBlur,
            ref: (el)=>{
                this.container = el;
            },
            onClick: this.handleRootClick
        }, /*#__PURE__*/ React.createElement(TextFieldType, {
            ...textfieldProps,
            readOnly: readOnly || !keyboardInput
        }));
        return this.props.inline ? calendar : /*#__PURE__*/ React.createElement("div", {
            className: cx('DatePicker', className)
        }, /*#__PURE__*/ React.createElement(Manager, null, /*#__PURE__*/ React.createElement(Reference, null, ({ ref })=>/*#__PURE__*/ React.createElement("div", {
                ref: ref
            }, textField)), this.state.open && /*#__PURE__*/ createPortal(/*#__PURE__*/ React.createElement(Popper, {
            referenceElement: this.state.inputReference,
            modifiers: popperModifiers,
            placement: datePickerPlacement
        }, ({ placement, ref, style })=>/*#__PURE__*/ React.createElement("div", {
                className: "react-datepicker-popper",
                ref: ref,
                style: style,
                "data-placement": placement
            }, calendar)), calendarTargetContainer || this.state.target)));
    }
}
DatePicker.propTypes = {
    /**
	 * True if async validation is in progress
	 */ asyncValidationInProgress: PropTypes.bool,
    /**
	 * Disable/enable native autocomple on inner input element
	 */ autoComplete: PropTypes.oneOf([
        'on',
        'off'
    ]),
    /**
	 * Element which will be used for rendering calendar component
	 */ calendarTargetContainer: PropTypes.element,
    /**
	 * The className of the datepicker.
	 */ className: PropTypes.string,
    /**
	 * If true, clear button is shown.
	 */ clearable: PropTypes.bool,
    /**
	 * The className of control section.
	 */ controlClassName: PropTypes.string,
    /**
	 * The className of control input field.
	 */ controlInputClassName: PropTypes.string,
    /**
	 * Icon for control panel when datepicker is open.
	 */ controlPanelIconClose: PropTypes.node,
    /**
	 * Icon for control panel when datepicker is closed.
	 */ controlPanelIconOpen: PropTypes.node,
    /**
	 * Date label format expression for left side controll panel.
	 */ dateFormat: PropTypes.string,
    /**
	 * Month and year label format expression for top controll panel.
	 */ dateFormatCalendar: PropTypes.string,
    /**
	 * For set popper placement
	 */ datePickerPlacement: PropTypes.string,
    /**
	 * Label for the day after tomorrow.
	 */ dayAfterTomorrowLabel: PropTypes.string,
    /**
	 * If true the datepicker will be disabled.
	 */ disabled: PropTypes.bool,
    /**
	 * End of highlited date range.
	 */ endDate: PropTypes.object,
    /**
	 * Array of dates that should be disabled for selection.
	 */ excludeDates: PropTypes.array,
    /**
	 * Function that enables you to enable certain dates according to specified criteria.
	 *
	 * @param {moment} date moment object which is tested to meet the criteria
	 * @return {bool} true/false'
	 */ filterDate: PropTypes.func,
    /**
	 * The className of floating label.
	 */ floatingLabelClassName: PropTypes.string,
    /**
	 * Text of floating label.
	 */ floatingLabelText: PropTypes.node,
    /**
	 * If true, calendar controls are hidden.
	 */ hideCalendarControls: PropTypes.bool,
    /**
	 * If true, validation message is hidden.
	 */ hideValidationText: PropTypes.bool,
    /**
	 * Form input id.
	 */ id: PropTypes.string,
    /**
	 * Array of dates which selection is enabled.
	 */ includeDates: PropTypes.array,
    /**
	 * Flag controlling whether to render calendar as inline version or using Popper
	 */ inline: PropTypes.bool,
    /**
	 * Whether datum can be typed using keyboard.
	 * If falsy only date selection using calendar is allowed.
	 */ keyboardInput: PropTypes.bool,
    /**
	 * The className of label.
	 */ labelClassName: PropTypes.string,
    /**
	 * Text over the datepicker text field and under the label text.
	 */ labelNote: PropTypes.node,
    /**
	 * 'left' or 'right' values for label positioning.
	 */ labelPosition: PropTypes.oneOf(FormGroup.labelPositions.values()),
    /**
	 * Text label over the datepicker text field.
	 */ labelText: PropTypes.node,
    /**
	 * Calendar locale.
	 */ locale: PropTypes.string,
    /**
	 * Max selectable date.
	 */ maxDate: PropTypes.object,
    /**
	 * Max length of input field
	 */ maxLength: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
    ]),
    /**
	 * Min selectable date.
	 */ minDate: PropTypes.object,
    /**
	 * Label for tomorrow.
	 */ nextDayLabel: PropTypes.string,
    /**
	 * Callback function that allows you to react on text field blur event.
	 *
	 * @param {Object} event blur event
	 * @return {null} Nothing
	 */ onBlur: PropTypes.func,
    /**
	 * Callback function that allows you to react on text field change event.
	 *
	 * @param {Object} event change event
	 * @return {null} Nothing
	 */ onChange: PropTypes.func,
    /**
	 * Callback function that allows you to react on click event.
	 *
	 * @param {Object} event click event
	 * @return {null} Nothing
	 */ onClick: PropTypes.func,
    /**
	 * Callback function that allow you validate field value after click on cross
	 * @param {Object} event click on cross event
	 * @return {null} Nothing
	 */ onClickPanelClear: PropTypes.func,
    /**
	 * Callback function that allows you to react on text field focus event.
	 *
	 * @param {Object} event focus event
	 * @return {null} Nothing
	 */ onFocus: PropTypes.func,
    /**
	 * Callback function that allows you to react on text field change event.
	 *
	 * @param {Object} event change event
	 * @return {null} Nothing
	 */ onLeave: PropTypes.func,
    /**
	 * Open calendar only by icon. (default false)
	 */ openCalendarByIcon: PropTypes.bool,
    /**
	 * The date to which datepicker should open.
	 */ openToDate: PropTypes.object,
    /**
	 * Wheter to use outlined Input or basic one
	 */ outline: PropTypes.bool,
    /**
	 * Placeholder value.
	 */ placeholder: PropTypes.string,
    /**
	 * Modifiers for popper
	 */ popperModifiers: PropTypes.arrayOf(PropTypes.object),
    /**
	 * If true calendar will refresh after input change (debounced with 1s)
	 */ reactive: PropTypes.bool,
    /**
	 * If true the datepicker will be read only.
	 */ readOnly: PropTypes.bool,
    /**
	 * Show whether Component is required
	 */ required: PropTypes.bool,
    /**
	 * Label for today.
	 */ sameDayLabel: PropTypes.string,
    /**
	 * If true, the dropdown with year selection is shown.
	 */ showYearDropdown: PropTypes.bool,
    /**
	 * If true, simpleDatePicker will be instead
	 */ simple: PropTypes.bool,
    /**
	 * If true, Select will be rendered with sizeSmall modifier
	 */ sizeSmall: PropTypes.bool,
    /**
	 * Start of highlited date range.
	 */ startDate: PropTypes.object,
    /**
	 * html tabindex property passed to wrapper element
	 */ tabIndex: PropTypes.number,
    /**
	 * Title of the datepicker.
	 */ title: PropTypes.string,
    /**
	 * If specified the button that can set today value will be shown with text you enter.
	 */ todayButton: PropTypes.string,
    /**
	 * This is Tooltip children
	 */ tooltip: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.element
    ]),
    /**
	 * The className of validation message.
	 */ validationClassName: PropTypes.string,
    /**
	 * Where to display validation
	 */ validationDisplayType: validationDisplayTypePropType,
    /**
	 * The validation text in case of validation error.
	 */ validationText: PropTypes.node,
    /**
	 * Value of the datepicker text field.
	 */ value: PropTypes.any
};
export { DatePicker };
export default withDefaultComponentProps(COMPONENT_NAME, {
    controlPanelIconOpen: /*#__PURE__*/ React.createElement(SvgCalendarIcon, null),
    controlPanelIconClose: /*#__PURE__*/ React.createElement(SvgCalendarIcon, null),
    value: '',
    keyboardInput: true,
    openCalendarByIcon: false,
    datePickerPlacement: 'bottom-start'
}, DatePicker);
